import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Info, Loader2, Save, Search, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { cn } from '../../../lib/utils';
import { SeverityCustomerPicker } from './SeverityCustomerPicker';
import {
  ALL_IDENTIFIERS_KEY,
  SeverityGroupPanel,
  UNGROUPED_KEY,
} from './SeverityGroupPanel';
import { SeverityCatalogRow, SeverityCatalogTable } from './SeverityCatalogTable';
import { SeverityGroupDialog, SeverityGroupFormValues } from './SeverityGroupDialog';
import { SeverityDeletionImpact, SeverityGroupDeleteDialog } from './SeverityGroupDeleteDialog';
import {
  apiService,
  Customer,
  ErrorEventSeverity,
  SeverityCatalogEntry,
  SeverityGroup,
  SeverityRulesResponse,
} from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  buildSeverityRuleIndex,
  countBucketRules,
  countPendingChanges,
  effectiveGroupId,
  EMPTY_PENDING_CHANGES,
  identifierBucket,
  pickIdentifierRule,
  resolveRuleService,
  resolveSeverity,
  SeverityPendingChanges,
  SeverityScope,
} from './severityResolution';

/** Aktion, die nach dem Verwerfen ungespeicherter Änderungen ausgeführt wird. */
type DeferredAction = { kind: 'scope'; scope: SeverityScope } | { kind: 'customer'; customer: Customer };

/**
 * Tab „Severity" — Pflege der Regeln, die die Dringlichkeit NEU eintreffender
 * Fehler-Events bestimmen.
 *
 * Aufbau: ein Geltungsbereich-Schalter (Global / Kunde) über einer
 * Master-Detail-Fläche. Der Kunden-Geltungsbereich ist derselbe Bildschirm mit
 * anderem Scope, keine zweite Oberfläche — dadurch ist die Vererbung
 * („global: Niedrig → für diesen Kunden: Kritisch") in derselben Zeile sichtbar.
 *
 * Änderungen an Regeln laufen über einen Puffer und werden gesammelt
 * gespeichert (kein Auto-Save): Severity steuert Alarmierung, ein verrutschter
 * Select darf die Rufbereitschaft nicht still umstellen. Gruppen-CRUD läuft
 * dagegen sofort über die Dialoge.
 */
export function SeverityConfigTab() {
  const [groups, setGroups] = useState<SeverityGroup[]>([]);
  const [catalog, setCatalog] = useState<SeverityCatalogEntry[]>([]);
  const [globalRules, setGlobalRules] = useState<SeverityRulesResponse | null>(null);
  const [customerRules, setCustomerRules] = useState<SeverityRulesResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [scope, setScope] = useState<SeverityScope>('global');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedKey, setSelectedKey] = useState<string>(ALL_IDENTIFIERS_KEY);
  const [pending, setPending] = useState<SeverityPendingChanges>(EMPTY_PENDING_CHANGES);

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SeverityGroup | null>(null);
  const [groupSubmitting, setGroupSubmitting] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<SeverityGroup | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [deferredAction, setDeferredAction] = useState<DeferredAction | null>(null);

  const pendingCount = countPendingChanges(pending);
  const isDirty = pendingCount > 0;
  const isCustomerScope = scope === 'customer';

  // ── Laden ────────────────────────────────────────────────────────
  const loadBaseData = useCallback(async () => {
    const [loadedGroups, loadedCatalog, loadedGlobalRules] = await Promise.all([
      apiService.getSeverityGroups(),
      apiService.getSeverityCatalog(),
      apiService.getSeverityRules(),
    ]);
    setGroups(loadedGroups);
    setCatalog(loadedCatalog);
    setGlobalRules(loadedGlobalRules);
  }, []);

  const loadAll = useCallback(
    async (customerId: string | null, silent: boolean) => {
      try {
        if (silent) setReloading(true);
        else setLoading(true);
        setError(null);
        await loadBaseData();
        setCustomerRules(customerId ? await apiService.getSeverityRules(customerId) : null);
      } catch (err) {
        setError('Severity-Konfiguration konnte nicht geladen werden');
        toast.error(extractErrorMessage(err, 'Severity-Konfiguration konnte nicht geladen werden'));
      } finally {
        setLoading(false);
        setReloading(false);
      }
    },
    [loadBaseData]
  );

  useEffect(() => {
    void loadAll(null, false);
  }, [loadAll]);

  // ── Ableitungen ──────────────────────────────────────────────────
  const groupsById = useMemo(() => new Map(groups.map((group) => [group.id, group])), [groups]);

  const ruleIndex = useMemo(
    () => buildSeverityRuleIndex(globalRules, isCustomerScope ? customerRules : null),
    [globalRules, customerRules, isCustomerScope]
  );

  const memberCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of catalog) {
      const groupId = effectiveGroupId(entry, pending);
      if (groupId) counts.set(groupId, (counts.get(groupId) ?? 0) + 1);
    }
    return counts;
  }, [catalog, pending]);

  const ungroupedCount = useMemo(
    () => catalog.filter((entry) => effectiveGroupId(entry, pending) === null).length,
    [catalog, pending]
  );

  /** Alle Katalog-Einträge als Tabellenzeilen — inklusive Auflösung und Puffer. */
  const allRows = useMemo<SeverityCatalogRow[]>(() => {
    return catalog.map((entry) => {
      const groupId = effectiveGroupId(entry, pending);
      const bucket = identifierBucket(ruleIndex, scope, entry.errorIdentifier);
      const ruleService = resolveRuleService(entry, bucket, pending);

      // Die Zeile zeigt die Regel auf der Ebene, auf die sie gerade verfeinert
      // ist: Wildcard oder genau ein Service.
      const storedSeverity =
        ruleService === null
          ? (bucket.wildcard?.severity ?? null)
          : (bucket.byService.get(ruleService)?.severity ?? null);
      const scopedSeverity =
        entry.errorIdentifier in pending.identifierRules
          ? pending.identifierRules[entry.errorIdentifier]
          : storedSeverity;

      const resolution = resolveSeverity({
        entry,
        index: ruleIndex,
        pending,
        scope,
        groupsById,
        ruleService,
      });

      // Vergleichswert für den Kunden-Scope: dieselbe Auflösung ohne Kunden-Stufen
      // und ohne Puffer, mit dem Verfeinerungs-Service der globalen Regel.
      const globalBucket = identifierBucket(ruleIndex, 'global', entry.errorIdentifier);
      const globalResolution = isCustomerScope
        ? resolveSeverity({
            entry,
            index: ruleIndex,
            pending: { ...pending, identifierRules: {}, groupRules: {}, ruleServices: {} },
            scope: 'global',
            groupsById,
            ruleService: pickIdentifierRule(globalBucket, entry.services)?.service ?? null,
          })
        : resolution;

      return {
        entry,
        groupId,
        groupName: groupId ? (groupsById.get(groupId)?.name ?? null) : null,
        resolution,
        scopedSeverity,
        globalSeverity: globalResolution.severity,
        ruleService,
        hasMultipleServiceRules: countBucketRules(bucket) > 1,
      };
    });
  }, [catalog, pending, ruleIndex, scope, isCustomerScope, groupsById]);

  const visibleRows = useMemo(() => {
    if (selectedKey === ALL_IDENTIFIERS_KEY) return allRows;
    if (selectedKey === UNGROUPED_KEY) return allRows.filter((row) => row.groupId === null);
    return allRows.filter((row) => row.groupId === selectedKey);
  }, [allRows, selectedKey]);

  const groupFilterLabel =
    selectedKey === ALL_IDENTIFIERS_KEY
      ? null
      : selectedKey === UNGROUPED_KEY
        ? 'Ohne Gruppe'
        : (groupsById.get(selectedKey)?.name ?? null);

  /**
   * Globale Severity einer Gruppe — im Kunden-Geltungsbereich die geerbte Basis.
   * Der Puffer zählt hier nur mit, solange global gearbeitet wird.
   */
  const groupGlobalSeverityOf = useCallback(
    (groupId: string): ErrorEventSeverity | null =>
      groupId in pending.groupRules && !isCustomerScope
        ? pending.groupRules[groupId]
        : (ruleIndex.globalGroup.get(groupId)?.severity ??
          groupsById.get(groupId)?.globalSeverity ??
          null),
    [pending.groupRules, isCustomerScope, ruleIndex, groupsById]
  );

  /** Severity einer Gruppe im aktiven Geltungsbereich — `null`, wenn dort keine Regel liegt. */
  const groupSeverityOf = useCallback(
    (groupId: string): ErrorEventSeverity | null => {
      if (groupId in pending.groupRules) return pending.groupRules[groupId];
      const rules = isCustomerScope ? ruleIndex.customerGroup : ruleIndex.globalGroup;
      return rules.get(groupId)?.severity ?? null;
    },
    [pending.groupRules, isCustomerScope, ruleIndex]
  );

  // ── Puffer-Mutationen ────────────────────────────────────────────
  const handleGroupSeverityChange = (groupId: string, severity: ErrorEventSeverity | null) => {
    setPending((prev) => ({ ...prev, groupRules: { ...prev.groupRules, [groupId]: severity } }));
  };

  const handleIdentifierSeverityChange = (
    errorIdentifier: string,
    severity: ErrorEventSeverity | null
  ) => {
    setPending((prev) => ({
      ...prev,
      identifierRules: { ...prev.identifierRules, [errorIdentifier]: severity },
    }));
  };

  const handleCatalogGroupChange = (errorIdentifier: string, groupId: string | null) => {
    setPending((prev) => ({
      ...prev,
      catalogGroups: { ...prev.catalogGroups, [errorIdentifier]: groupId },
    }));
  };

  /**
   * Verfeinert die Regel auf einen Service oder setzt sie auf die Wildcard
   * zurück. Das verschiebt die Regel und ist damit eine eigenständige Änderung —
   * auch wenn die Severity gleich bleibt, muss sie im Puffer landen, sonst
   * bliebe der Wechsel beim Speichern wirkungslos.
   */
  const handleRuleServiceChange = (errorIdentifier: string, service: string | null) => {
    const row = allRows.find((candidate) => candidate.entry.errorIdentifier === errorIdentifier);
    setPending((prev) => ({
      ...prev,
      ruleServices: { ...prev.ruleServices, [errorIdentifier]: service },
      identifierRules:
        errorIdentifier in prev.identifierRules || !row?.scopedSeverity
          ? prev.identifierRules
          : { ...prev.identifierRules, [errorIdentifier]: row.scopedSeverity },
    }));
  };

  const discardPending = () => setPending(EMPTY_PENDING_CHANGES);

  // ── Geltungsbereich wechseln (mit Verwerfen-Rückfrage) ───────────
  const applyDeferredAction = (action: DeferredAction) => {
    discardPending();
    if (action.kind === 'scope') {
      setScope(action.scope);
      if (action.scope === 'global') {
        setCustomerRules(null);
      } else if (selectedCustomer) {
        void apiService
          .getSeverityRules(selectedCustomer.id)
          .then(setCustomerRules)
          .catch((err: unknown) =>
            toast.error(extractErrorMessage(err, 'Kunden-Regeln konnten nicht geladen werden'))
          );
      }
      return;
    }
    setSelectedCustomer(action.customer);
    void apiService
      .getSeverityRules(action.customer.id)
      .then(setCustomerRules)
      .catch((err: unknown) =>
        toast.error(extractErrorMessage(err, 'Kunden-Regeln konnten nicht geladen werden'))
      );
  };

  const requestAction = (action: DeferredAction) => {
    if (isDirty) {
      setDeferredAction(action);
      return;
    }
    applyDeferredAction(action);
  };

  // ── Speichern ────────────────────────────────────────────────────
  const handleSave = async () => {
    const customerId = isCustomerScope ? (selectedCustomer?.id ?? null) : null;
    if (isCustomerScope && !customerId) {
      toast.error('Kein Kunde ausgewählt');
      return;
    }

    try {
      setSaving(true);

      // 1. Gruppen-Zuordnungen des Katalogs (nicht kundenspezifisch).
      for (const [errorIdentifier, groupId] of Object.entries(pending.catalogGroups)) {
        await apiService.setSeverityCatalogGroup(errorIdentifier, groupId);
      }

      // 2. Gruppen-Regeln im aktiven Geltungsbereich.
      const groupRules = isCustomerScope ? ruleIndex.customerGroup : ruleIndex.globalGroup;
      for (const [groupId, severity] of Object.entries(pending.groupRules)) {
        const existing = groupRules.get(groupId);
        if (severity === null) {
          if (existing) await apiService.deleteSeverityGroupRule(existing.id);
        } else {
          await apiService.upsertSeverityGroupRule({ customerId, groupId, severity });
        }
      }

      // 3. Identifier-Regeln im aktiven Geltungsbereich. `service: null` ist die
      //    Wildcard und der Normalfall; ein gesetzter Service verfeinert.
      for (const [errorIdentifier, severity] of Object.entries(pending.identifierRules)) {
        const row = allRows.find((candidate) => candidate.entry.errorIdentifier === errorIdentifier);
        const bucket = identifierBucket(ruleIndex, scope, errorIdentifier);
        const previous = pickIdentifierRule(bucket, row?.entry.services ?? []);

        if (severity === null) {
          if (previous) await apiService.deleteSeverityIdentifierRule(previous.id);
          continue;
        }

        const service = row?.ruleService ?? null;
        // Wurde die Verfeinerung gewechselt, wird die Regel verschoben statt
        // dupliziert — sonst blieben Wildcard und Service-Regel nebeneinander stehen.
        if (previous && previous.service !== service) {
          await apiService.deleteSeverityIdentifierRule(previous.id);
        }
        await apiService.upsertSeverityIdentifierRule({
          customerId,
          service,
          errorIdentifier,
          severity,
        });
      }

      toast.success(`${pendingCount} Änderungen gespeichert — wirkt auf neu eintreffende Events`);
      discardPending();
      await loadAll(customerId, true);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Änderungen konnten nicht gespeichert werden'));
    } finally {
      setSaving(false);
    }
  };

  // ── Gruppen-CRUD (sofort, nicht gepuffert) ───────────────────────
  const handleGroupSubmit = async (values: SeverityGroupFormValues) => {
    try {
      setGroupSubmitting(true);
      const target = editingGroup
        ? await apiService.updateSeverityGroup(editingGroup.id, {
            name: values.name,
            description: values.description,
          })
        : await apiService.createSeverityGroup({
            name: values.name,
            description: values.description ?? undefined,
          });

      // Die Default-Severity ist serverseitig eine eigene globale Gruppen-Regel.
      const existingRule = ruleIndex.globalGroup.get(target.id);
      if (values.globalSeverity !== null) {
        await apiService.upsertSeverityGroupRule({
          customerId: null,
          groupId: target.id,
          severity: values.globalSeverity,
        });
      } else if (existingRule) {
        await apiService.deleteSeverityGroupRule(existingRule.id);
      }

      toast.success(editingGroup ? 'Gruppe aktualisiert' : 'Gruppe angelegt');
      setGroupDialogOpen(false);
      setEditingGroup(null);
      await loadAll(isCustomerScope ? (selectedCustomer?.id ?? null) : null, true);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Gruppe konnte nicht gespeichert werden'));
    } finally {
      setGroupSubmitting(false);
    }
  };

  /** Folgen-Vorschau: wie die Mitglieder eingestuft würden, wenn die Gruppe wegfällt. */
  const deletionImpacts = useMemo<SeverityDeletionImpact[]>(() => {
    if (!deletingGroup) return [];
    return catalog
      .filter((entry) => effectiveGroupId(entry, pending) === deletingGroup.id)
      .map((entry) => {
        const ruleService = resolveRuleService(
          entry,
          identifierBucket(ruleIndex, scope, entry.errorIdentifier),
          pending
        );
        const before = resolveSeverity({
          entry,
          index: ruleIndex,
          pending,
          scope,
          groupsById,
          ruleService,
        });
        const after = resolveSeverity({
          entry,
          index: ruleIndex,
          pending: {
            ...pending,
            catalogGroups: { ...pending.catalogGroups, [entry.errorIdentifier]: null },
          },
          scope,
          groupsById,
          ruleService,
        });
        return {
          errorIdentifier: entry.errorIdentifier,
          before: before.severity,
          after: after.severity,
          unchanged: before.severity === after.severity,
        };
      });
  }, [deletingGroup, catalog, pending, ruleIndex, scope, groupsById]);

  const handleGroupDelete = async () => {
    if (!deletingGroup) return;
    try {
      setGroupSubmitting(true);
      await apiService.deleteSeverityGroup(deletingGroup.id);
      toast.success(`Gruppe „${deletingGroup.name}" gelöscht`);
      if (selectedKey === deletingGroup.id) setSelectedKey(ALL_IDENTIFIERS_KEY);
      setDeleteDialogOpen(false);
      setDeletingGroup(null);
      await loadAll(isCustomerScope ? (selectedCustomer?.id ?? null) : null, true);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Gruppe konnte nicht gelöscht werden'));
    } finally {
      setGroupSubmitting(false);
    }
  };

  // ── Rendering ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
        <span>Severity-Konfiguration wird geladen...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-12 text-red-600">
        <AlertCircle className="mr-2 h-5 w-5" />
        <span>{error}</span>
        <Button variant="outline" size="sm" onClick={() => void loadAll(null, false)} className="ml-4">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  const showCustomerPlaceholder = isCustomerScope && !selectedCustomer;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <Info className="mt-0.5 h-4 w-4 flex-none" />
        <span>Regeländerungen wirken nur auf neu eintreffende Events, nie rückwirkend.</span>
      </div>

      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-xl border bg-white px-4 py-3',
          isCustomerScope && 'border-amber-200 bg-amber-50/30'
        )}
      >
        <span className="text-sm font-medium text-gray-700">Geltungsbereich</span>
        <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-1">
          {(['global', 'customer'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => requestAction({ kind: 'scope', scope: value })}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                scope === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {value === 'global' ? 'Global' : 'Kunde'}
            </button>
          ))}
        </div>

        {isCustomerScope && (
          <SeverityCustomerPicker
            selectedCustomer={selectedCustomer}
            onSelect={(customer) => requestAction({ kind: 'customer', customer })}
          />
        )}

        <span className="ml-auto text-xs text-gray-500">
          {isCustomerScope
            ? 'Alles ohne Override erbt die globalen Regeln'
            : 'Gilt für alle Kunden ohne eigenen Override'}
        </span>
      </div>

      {showCustomerPlaceholder ? (
        <Card>
          <CardContent className="py-14 text-center">
            <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
              <Search className="h-5 w-5" />
            </span>
            <div className="text-sm font-semibold text-gray-800">Kunde auswählen</div>
            <p className="mx-auto mt-1.5 max-w-md text-sm text-gray-500">
              Such einen Kunden, um dessen Abweichungen von den globalen Regeln zu sehen und zu bearbeiten.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
          <SeverityGroupPanel
            groups={groups}
            scope={scope}
            selectedKey={selectedKey}
            onSelectKey={setSelectedKey}
            memberCounts={memberCounts}
            ungroupedCount={ungroupedCount}
            globalSeverityOf={groupGlobalSeverityOf}
            scopedSeverityOf={groupSeverityOf}
            onSeverityChange={handleGroupSeverityChange}
            onCreateGroup={() => {
              setEditingGroup(null);
              setGroupDialogOpen(true);
            }}
            onEditGroup={(group) => {
              setEditingGroup(group);
              setGroupDialogOpen(true);
            }}
            onDeleteGroup={(group) => {
              setDeletingGroup(group);
              setDeleteDialogOpen(true);
            }}
          />

          <SeverityCatalogTable
            rows={visibleRows}
            groups={groups}
            scope={scope}
            customerName={selectedCustomer?.name}
            catalogTotal={catalog.length}
            groupFilterLabel={groupFilterLabel}
            onClearGroupFilter={() => setSelectedKey(ALL_IDENTIFIERS_KEY)}
            onGroupChange={handleCatalogGroupChange}
            onSeverityChange={handleIdentifierSeverityChange}
            onRuleServiceChange={handleRuleServiceChange}
            groupSeverityOf={groupSeverityOf}
            groupGlobalSeverityOf={groupGlobalSeverityOf}
            onGroupSeverityChange={handleGroupSeverityChange}
            onReload={() =>
              void loadAll(isCustomerScope ? (selectedCustomer?.id ?? null) : null, true)
            }
            reloading={reloading}
          />
        </div>
      )}

      {isDirty && (
        <div className="sticky bottom-4 flex justify-center">
          <div className="flex items-center gap-4 rounded-xl bg-gray-900 px-5 py-3 text-white shadow-lg">
            <Save className="h-4 w-4 flex-none" />
            <div>
              <div className="text-sm font-medium">
                {pendingCount} {pendingCount === 1 ? 'Änderung' : 'Änderungen'}
                {isCustomerScope && selectedCustomer ? ` für ${selectedCustomer.name}` : ' im globalen Geltungsbereich'}
              </div>
              <div className="text-xs text-gray-400">
                Wirkt ab dem Speichern auf neu eintreffende Events
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={discardPending}
              disabled={saving}
              className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Verwerfen
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-white text-gray-900 hover:bg-gray-100"
            >
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Änderungen speichern
            </Button>
          </div>
        </div>
      )}

      <SeverityGroupDialog
        open={groupDialogOpen}
        onOpenChange={(open) => {
          setGroupDialogOpen(open);
          if (!open) setEditingGroup(null);
        }}
        group={editingGroup}
        submitting={groupSubmitting}
        onSubmit={handleGroupSubmit}
      />

      <SeverityGroupDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeletingGroup(null);
        }}
        group={deletingGroup}
        impacts={deletionImpacts}
        // TODO: echte Zahl über alle Kunden — dafür fehlt ein Zähl-Endpoint.
        // Bekannt ist nur der aktuell geladene Kunden-Geltungsbereich.
        customerOverrideCount={
          deletingGroup && ruleIndex.customerGroup.has(deletingGroup.id) ? 1 : 0
        }
        submitting={groupSubmitting}
        onConfirm={handleGroupDelete}
      />

      <AlertDialog
        open={deferredAction !== null}
        onOpenChange={(open) => {
          if (!open) setDeferredAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ungespeicherte Änderungen verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>
              Im aktuellen Geltungsbereich liegen {pendingCount}{' '}
              {pendingCount === 1 ? 'ungespeicherte Änderung' : 'ungespeicherte Änderungen'}. Beim Wechsel
              gehen sie verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hier bleiben</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deferredAction) applyDeferredAction(deferredAction);
                setDeferredAction(null);
              }}
            >
              Verwerfen und wechseln
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isCustomerScope && selectedCustomer && (
        <div className="flex justify-end">
          <Badge variant="outline" className="font-normal text-gray-500">
            Kunde: {selectedCustomer.name}
          </Badge>
        </div>
      )}
    </div>
  );
}
